/**
 * The two modals a theme row opens.
 *
 * A row makes two different claims and each can be questioned separately, so each
 * gets its own surface rather than one panel with two headings:
 *
 * - the **count** ("12 positions") is a claim about *this book now*, and the
 *   positions modal answers it with the names and the amount each contributes.
 *   The list is rendered from the row the user tapped, so it cannot disagree with
 *   the number they tapped — it is the same payload.
 * - the **name** is a claim about *the theme*, and the thesis modal answers it with
 *   the operator's own investment case and its break conditions, fetched by slug
 *   and shown verbatim.
 *
 * The thesis is English-only: the registry stores one authored paragraph and has no
 * translation table for it. Showing it untranslated is the honest option — the
 * alternatives are paraphrasing someone's investment case in the client or
 * machine-translating it, and both produce a *different* investment case.
 *
 * Neither dialog dismisses on a backdrop press. A single tap on the dimmed area is
 * easy to land by accident while scrolling a long list, and losing the panel is
 * more annoying than tapping Close.
 */

import React, { useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { ThemeDetailNotFoundError, type ThemeBreakCondition } from '../../services/api/themeApi';
import { useThemeDetail } from '../../services/api/queries/useThemeTags';
import type { ThemeBreakdownConstituent } from '../../services/portfolioApi';
import { useTheme } from '../../contexts/ThemeContext';
import { formatAmount } from '../../utils/formatAmount';

export interface BreakConditionLine {
  /** Stable across renders: the registry's own id where it has one. */
  key: string;
  text: string;
  /** `approaching` | `fired` | null. Anything else is not rendered as a label. */
  status: string | null;
}

/**
 * A stable key for one position in the list.
 *
 * The symbol cannot be one: it is the instrument's *canonical* symbol while
 * positions carry local codes, and instruments are only unique on
 * `(symbol, currency)` — so two positions in one theme can share it. `position_id`
 * is the position's own identity; the composite fallback exists only for an older
 * API that does not send it, and the index makes even that unique.
 */
export function holdingKey(holding: ThemeBreakdownConstituent, index: number): string {
  if (holding.position_id != null) return `position-${holding.position_id}`;
  return `${holding.symbol}-${holding.value}-${index}`;
}

/**
 * Break conditions in either shape the registry has used: a bare string (the
 * legacy form, still on live rows) or an object with an id, text and reading.
 * Blank entries are dropped rather than rendered as an empty bullet.
 */
export function normalizeBreakConditions(
  raw: ThemeBreakCondition[] | null | undefined
): BreakConditionLine[] {
  return (raw ?? [])
    .map((entry, index): BreakConditionLine => {
      if (typeof entry === 'string') {
        return { key: `c${index}`, text: entry.trim(), status: null };
      }
      return {
        key: entry?.id || `c${index}`,
        text: (entry?.text ?? '').trim(),
        status: entry?.status ?? null,
      };
    })
    .filter((line) => line.text.length > 0);
}

/** The two states the ledger records that a reader must not miss. */
const CONDITION_STATUS_KEYS: Record<string, string> = {
  approaching: 'dashboard.themeBreakConditionApproaching',
  fired: 'dashboard.themeBreakConditionFired',
};

interface SheetProps {
  visible: boolean;
  title: string;
  subtitle?: string;
  testID: string;
  onClose: () => void;
  children: React.ReactNode;
}

/** Shared chrome: title, scrolling body, one way out. */
function Sheet({ visible, title, subtitle, testID, onClose, children }: SheetProps) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
      testID={testID}
    >
      <View style={styles.overlay}>
        <View style={styles.dialog}>
          <Text style={[styles.title, { color: theme.colors.text }]}>{title}</Text>
          {subtitle ? (
            <Text style={[styles.subtitle, { color: theme.colors.textSecondary }]}>{subtitle}</Text>
          ) : null}

          <ScrollView style={styles.body} contentContainerStyle={styles.bodyContent}>
            {children}
          </ScrollView>

          <TouchableOpacity
            style={[
              styles.closeButton,
              {
                backgroundColor: theme.colors.backgroundElevated,
                borderColor: theme.colors.border,
              },
            ]}
            onPress={onClose}
            testID={`${testID}-close`}
          >
            <Text style={[styles.closeText, { color: theme.colors.textSecondary }]}>
              {t('common.close', { defaultValue: 'Close' })}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
}

export interface ThemeHoldingsModalProps {
  visible: boolean;
  /** Already localised by the caller, which owns the registry lookup. */
  name: string;
  count: number;
  holdings: ThemeBreakdownConstituent[];
  currency: string | null;
  onClose: () => void;
}

/** Which positions a theme's number is made of. */
export function ThemeHoldingsModal({
  visible,
  name,
  count,
  holdings,
  currency,
  onClose,
}: ThemeHoldingsModalProps) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  return (
    <Sheet
      visible={visible}
      title={name}
      subtitle={t('dashboard.themeExposurePositions', {
        defaultValue: '{{count}} position(s)',
        count,
      })}
      testID="theme-holdings-modal"
      onClose={onClose}
    >
      {holdings.length === 0 ? (
        <Text style={[styles.emptyText, { color: theme.colors.textSecondary }]}>
          {t('dashboard.themeHoldingsEmpty', { defaultValue: 'No positions in this theme' })}
        </Text>
      ) : (
        holdings.map((holding, index) => (
          <View
            key={holdingKey(holding, index)}
            style={[styles.holdingRow, { borderBottomColor: theme.colors.border }]}
            // Symbol-based on purpose: readable in a screen query, and a duplicate
            // only when two positions share a canonical symbol — a case the *key*
            // has to survive, not the testID.
            testID={`theme-holding-${holding.symbol}`}
          >
            <View style={styles.holdingIdentity}>
              <Text style={[styles.holdingSymbol, { color: theme.colors.text }]}>
                {holding.symbol}
              </Text>
              {holding.name ? (
                <Text
                  style={[styles.holdingName, { color: theme.colors.textSecondary }]}
                  numberOfLines={1}
                >
                  {holding.name}
                </Text>
              ) : null}
            </View>
            <Text
              style={[styles.holdingValue, { color: theme.colors.textSecondary }]}
              testID={`theme-holding-value-${holding.symbol}`}
            >
              {formatAmount(holding.value, currency)}
            </Text>
          </View>
        ))
      )}
    </Sheet>
  );
}

export interface ThemeThesisModalProps {
  visible: boolean;
  /** `null` while nothing is open; the query does not run until it is set. */
  slug: string | null;
  /** Already localised theme name. */
  name: string;
  onClose: () => void;
}

/** The theme's own investment case, and what would break it. */
export function ThemeThesisModal({ visible, slug, name, onClose }: ThemeThesisModalProps) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);
  const detailQuery = useThemeDetail(visible ? slug : null);

  const detail = detailQuery.data ?? null;
  const conditions = normalizeBreakConditions(detail?.thesis_break_conditions);

  return (
    <Sheet
      visible={visible}
      title={name}
      subtitle={
        detail?.conviction_level
          ? t('dashboard.themeConviction', {
              defaultValue: 'Conviction {{level}}/5',
              level: detail.conviction_level,
            })
          : undefined
      }
      testID="theme-thesis-modal"
      onClose={onClose}
    >
      {detailQuery.isLoading ? (
        <ActivityIndicator size="small" color={theme.colors.primary} />
      ) : detailQuery.error instanceof ThemeDetailNotFoundError ? (
        // The wiki answered "gone" — an archived theme whose tags are still live can
        // be opened from a row, so this is a real state and not a failed request.
        <Text
          style={[styles.emptyText, { color: theme.colors.textSecondary }]}
          testID="theme-thesis-gone"
        >
          {t('dashboard.themeThesisGone', {
            defaultValue: 'This theme is no longer in the registry.',
          })}
        </Text>
      ) : detailQuery.isError ? (
        <Text
          style={[styles.emptyText, { color: theme.colors.textSecondary }]}
          testID="theme-thesis-error"
        >
          {t('dashboard.themeThesisFailed', { defaultValue: 'Could not load the theme thesis' })}
        </Text>
      ) : (
        <>
          <Text style={[styles.sectionLabel, { color: theme.colors.textTertiary }]}>
            {t('dashboard.themeThesis', { defaultValue: 'Investment thesis' })}
          </Text>
          <Text style={[styles.paragraph, { color: theme.colors.text }]} testID="theme-thesis-text">
            {detail?.thesis?.trim() ||
              t('dashboard.themeThesisEmpty', {
                defaultValue: 'No thesis recorded for this theme yet.',
              })}
          </Text>

          {/* The operator's own reason for the conviction. It travels with the
              level because a number out of five with no stated basis is the kind of
              precision that invites over-reading. */}
          {detail?.conviction_reason ? (
            <Text
              style={[styles.paragraph, { color: theme.colors.textSecondary }]}
              testID="theme-conviction-reason"
            >
              {detail.conviction_reason}
            </Text>
          ) : null}

          {conditions.length > 0 ? (
            <>
              <Text style={[styles.sectionLabel, { color: theme.colors.textTertiary }]}>
                {t('dashboard.themeBreakConditions', { defaultValue: 'Break conditions' })}
              </Text>
              {conditions.map((condition) => {
                const statusKey = condition.status
                  ? CONDITION_STATUS_KEYS[condition.status]
                  : undefined;
                return (
                  <View key={condition.key} style={styles.conditionRow}>
                    <Text style={[styles.bullet, { color: theme.colors.textTertiary }]}>•</Text>
                    <Text
                      style={[styles.conditionText, { color: theme.colors.textSecondary }]}
                      testID={`theme-break-condition-${condition.key}`}
                    >
                      {condition.text}
                      {statusKey ? (
                        <Text style={{ color: theme.colors.error }}>
                          {'  '}
                          {t(statusKey)}
                        </Text>
                      ) : null}
                    </Text>
                  </View>
                );
              })}
            </>
          ) : null}
        </>
      )}
    </Sheet>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    overlay: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 20,
    },
    dialog: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.radii.lg,
      padding: 20,
      width: '100%',
      maxWidth: 420,
      // Bounded so a theme with forty names scrolls inside the dialog instead of
      // growing past the screen; the number is a layout constant, not a token.
      maxHeight: '85%',
      borderWidth: theme.id === 'bauhaus' ? 2 : 0,
      borderColor: theme.colors.border,
    },
    title: {
      fontSize: theme.typography.fontSizeLg,
      fontWeight: theme.typography.fontWeightBold,
    },
    subtitle: {
      fontSize: theme.typography.fontSizeSm,
      marginTop: 2,
      marginBottom: 8,
    },
    body: {
      marginTop: 8,
    },
    bodyContent: {
      paddingBottom: 4,
    },
    holdingRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: 10,
      borderBottomWidth: 1,
    },
    holdingIdentity: {
      flex: 1,
      marginRight: 12,
    },
    holdingSymbol: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    holdingName: {
      fontSize: theme.typography.fontSizeXs,
    },
    holdingValue: {
      fontSize: theme.typography.fontSizeSm,
    },
    sectionLabel: {
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      marginTop: 12,
      marginBottom: 4,
    },
    paragraph: {
      fontSize: theme.typography.fontSizeSm,
      lineHeight: theme.typography.fontSizeSm * 1.5,
    },
    conditionRow: {
      flexDirection: 'row',
      marginTop: 6,
    },
    bullet: {
      fontSize: theme.typography.fontSizeSm,
      marginRight: 6,
    },
    conditionText: {
      flex: 1,
      fontSize: theme.typography.fontSizeSm,
      lineHeight: theme.typography.fontSizeSm * 1.4,
    },
    emptyText: {
      fontSize: theme.typography.fontSizeSm,
      textAlign: 'center',
      paddingVertical: 12,
    },
    closeButton: {
      marginTop: 16,
      paddingVertical: 12,
      borderRadius: theme.radii.md,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      alignItems: 'center',
      minHeight: 48,
      justifyContent: 'center',
    },
    closeText: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
  });
}
